/**
 * Feature Icons
 * 
 * Flat SVG icons matching the twin frontend's style.
 * Uses stroke-based design with subtle fill for depth.
 */

import React from 'react'

interface IconProps {
  size?: number
  color?: string
  className?: string
}

// Analytics/Chart Icon
export const AnalyticsIcon: React.FC<IconProps> = ({ size = 24, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="12" width="4" height="8" rx="1" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" />
    <rect x="10" y="8" width="4" height="12" rx="1" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" />
    <rect x="16" y="4" width="4" height="16" rx="1" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" />
  </svg>
)

// Power/Lightning Icon
export const PowerIcon: React.FC<IconProps> = ({ size = 24, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
      fill={color}
      fillOpacity="0.15"
    />
    <path
      d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

// Environmental/Leaf Icon
export const EnvironmentalIcon: React.FC<IconProps> = ({ size = 24, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.5 21C3.5 17.5 3 12.5 5 8c2.5-.5 5 1 7.5 4 2.5-3 5-4.5 7.5-4 2 4.5 1.5 9.5-1.5 13"
      fill={color}
      fillOpacity="0.15"
    />
    <path
      d="M6.5 21C3.5 17.5 3 12.5 5 8c2.5-.5 5 1 7.5 4 2.5-3 5-4.5 7.5-4 2 4.5 1.5 9.5-1.5 13"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M12 12v9" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

// Alert/Bell Icon
export const AlertIcon: React.FC<IconProps> = ({ size = 24, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"
      fill={color}
      fillOpacity="0.15"
    />
    <path
      d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.73 21a2 2 0 01-3.46 0"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

// Integration/Network Icon
export const IntegrationIcon: React.FC<IconProps> = ({ size = 24, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="5" r="3" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" />
    <circle cx="5" cy="19" r="3" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" />
    <circle cx="19" cy="19" r="3" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" />
    <path d="M12 8v4M8.5 14.5l-2 2.5M15.5 14.5l2 2.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="12" cy="14" r="2" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" />
  </svg>
)

// Audio/Sound Icon
export const AudioIcon: React.FC<IconProps> = ({ size = 24, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3 18v-6a9 9 0 1118 0v6"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <rect x="3" y="14" width="4" height="6" rx="1" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" />
    <rect x="17" y="14" width="4" height="6" rx="1" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" />
  </svg>
)

// AI/Brain Icon
export const AIIcon: React.FC<IconProps> = ({ size = 24, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 4.5a7.5 7.5 0 017.5 7.5c0 2.5-1.5 4.5-3 6s-3 3-4.5 3-3-1.5-4.5-3-3-3.5-3-6A7.5 7.5 0 0112 4.5z"
      fill={color}
      fillOpacity="0.15"
    />
    <path
      d="M12 4.5a7.5 7.5 0 017.5 7.5c0 2.5-1.5 4.5-3 6s-3 3-4.5 3-3-1.5-4.5-3-3-3.5-3-6A7.5 7.5 0 0112 4.5z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M12 4.5V2M8.5 6l-1.5-1M15.5 6l1.5-1" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="9" cy="10" r="1" fill={color} />
    <circle cx="15" cy="10" r="1" fill={color} />
    <path d="M9 14c.5 1 1.5 1.5 3 1.5s2.5-.5 3-1.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

// Insights/Eye Icon
export const InsightsIcon: React.FC<IconProps> = ({ size = 24, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
      fill={color}
      fillOpacity="0.15"
    />
    <path
      d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" />
  </svg>
)

// Vision/Camera Icon
export const VisionIcon: React.FC<IconProps> = ({ size = 24, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="6" width="20" height="13" rx="2" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" />
    <circle cx="12" cy="12.5" r="3.5" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" />
    <circle cx="12" cy="12.5" r="1.5" fill={color} />
    <path d="M5 6V4a1 1 0 011-1h3" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

// Crowd/People Icon
export const CrowdIcon: React.FC<IconProps> = ({ size = 24, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="5" r="2.5" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" />
    <circle cx="5" cy="8" r="2" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" />
    <circle cx="19" cy="8" r="2" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" />
    <path d="M8 21v-4a4 4 0 018 0v4" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M1 21v-3a3 3 0 013-3h2" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M23 21v-3a3 3 0 00-3-3h-2" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

// Shield/Security Icon
export const SecurityIcon: React.FC<IconProps> = ({ size = 24, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 3L4 7v5c0 5.25 3.4 10.15 8 11.25 4.6-1.1 8-6 8-11.25V7l-8-4z"
      fill={color}
      fillOpacity="0.15"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M9 12l2 2 4-4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

// Hard Hat/PPE Icon
export const PPEIcon: React.FC<IconProps> = ({ size = 24, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 15a8 8 0 0116 0"
      fill={color}
      fillOpacity="0.15"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path d="M2 15h20" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M4 15v2a2 2 0 002 2h12a2 2 0 002-2v-2" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M12 4v4M8 6l1 2M16 6l-1 2" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
  </svg>
)

// Building/Facility Icon
export const BuildingIcon: React.FC<IconProps> = ({ size = 24, color = 'currentColor', className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="18" rx="2" fill={color} fillOpacity="0.15" stroke={color} strokeWidth="1.5" />
    <path d="M9 21V12h6v9" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="7" y="7" width="3" height="2" fill={color} />
    <rect x="14" y="7" width="3" height="2" fill={color} />
  </svg>
)
